import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import dayjs from "dayjs";
import type { WontSendReason } from "./appointmentNotificationRows";

/**
 * A location wall-clock time ("YYYY-MM-DDTHH:mm") relative to `now`, lower-case
 * so it reads mid-sentence: "danes ob 19:00", "jutri ob 13:00", "čet., 24. 9. ob 21:00".
 */
export function formatSendTime(wallClock: string, now: Date): string {
  const at = dayjs(wallClock);
  const time = at.format("HH:mm");
  const dayDiff = at.startOf("day").diff(dayjs(now).startOf("day"), "day");

  if (dayDiff === 0) return t`danes ob ${time}`;
  if (dayDiff === 1) return t`jutri ob ${time}`;
  if (dayDiff === -1) return t`včeraj ob ${time}`;

  const date = i18n.date(at.toDate(), {
    weekday: "short",
    day: "numeric",
    month: "numeric",
    ...(at.year() === dayjs(now).year() ? {} : { year: "numeric" }),
  });
  return t`${date} ob ${time}`;
}

/** A location wall-clock time ("YYYY-MM-DDTHH:mm") as a fixed date: "tor., 3. 11. 2026 ob 17:00". */
export function formatAppointmentTime(wallClock: string): string {
  const at = dayjs(wallClock);
  const time = at.format("HH:mm");
  const date = i18n.date(at.toDate(), {
    weekday: "short",
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
  return t`${date} ob ${time}`;
}

export function capitalizeFirst(text: string): string {
  return text.charAt(0).toLocaleUpperCase(i18n.locale) + text.slice(1);
}

export function wontSendReasonText({
  reason,
  sendAt,
  decidedAt,
  now,
}: {
  reason: WontSendReason;
  sendAt: string;
  decidedAt: string | null;
  now: Date;
}): string {
  const send = formatSendTime(sendAt, now);
  const decided = decidedAt ? formatSendTime(decidedAt, now) : null;

  switch (reason) {
    case "savedLate":
      return decided
        ? t`Čas pošiljanja: ${send}. Termin shranjen: ${decided}.`
        : t`Čas pošiljanja: ${send}. Termin je bil shranjen prepozno.`;
    case "settingChangedLate":
      return decided
        ? t`Čas pošiljanja: ${send}. Nastavitev spremenjena: ${decided}.`
        : t`Čas pošiljanja: ${send}. Nastavitev je bila spremenjena prepozno.`;
    case "movedLate":
      return t`Čas pošiljanja: ${send}. Termin je bil prestavljen prepozno.`;
  }
}
